/**
 * Hacer una fase entera del plan (M-5, 3-oct-2026).
 *
 * Rais: las pasadas se ejecutan «fase a fase». Las de una fase se hacen seguidas —cada una sobre lo
 * que deja la anterior— y sale UNA versión para revisar en Word, donde cada pasada es un revisor
 * distinto. Antes de pulsar: qué pasadas lleva y cuánto cuesta. Después: la versión nueva y qué hacer
 * en Word. Si el motor para (documento abierto, cambios sin decidir, plan sin aprobar), se dice con
 * sus palabras y no se gasta nada.
 */
import { useEffect, useState } from "react";
import { PuertaPasada, dolares, ejecutarFase } from "../api";
import type { FasePrevia, ModeloCatalogo, Trabajo } from "../api";
import { avisosDeFaseHecha, comoDecidirEnWord, estimarCoste, lineaDePasadaDeFase, nombreDeRuta } from "../logica";
import type { ResultadoDeFase } from "../logica";
import { ListaDePasos } from "./ListaDePasos";

export default function EjecutarFase({ previa, sinLeer = false, alReintentar, carpeta, doc, trabajo, precio, alLanzar, alHablar, alIr, alSeguirCon, alIrDeUnaEnUna }: {
  /** Lo que dice el motor de la fase que toca. `null`: todavía no ha contestado. */
  previa: FasePrevia | null;
  /** El motor no ha contestado bien (no es «motor anterior»): se dice y se ofrece salida. */
  sinLeer?: boolean;
  alReintentar?: () => void;
  carpeta: string;
  doc: string | null;
  trabajo: Trabajo | null;
  precio: ModeloCatalogo | undefined;
  alLanzar: () => void;
  alHablar: () => void;
  alIr: (s: string) => void;
  /** La versión que acaba de nacer pasa a ser el manuscrito: la fase siguiente sale de ella. */
  alSeguirCon?: (ruta: string) => void;
  /** Hacer solo una pasada, como antes. */
  alIrDeUnaEnUna: () => void;
}) {
  const [ocupado, setOcupado] = useState(false);
  // Un RECHAZO, no una nota: la fase no se ha hecho y hay que hacer algo antes.
  const [puerta, setPuerta] = useState<string | null>(null);
  const enCurso = trabajo?.estado === "en_curso";

  async function hacer() {
    if (!doc || previa?.fase == null) return;
    setOcupado(true); setPuerta(null);
    try { await ejecutarFase(carpeta, doc, previa.fase); alLanzar(); }
    catch (e) {
      // «Trabajas sobre una versión vieja» no lo arregla el autor: la fase se hace sobre la última.
      if (e instanceof PuertaPasada && e.codigo === "no_es_la_ultima" && e.ultima && carpeta) {
        const ruta = `${carpeta}/${e.ultima}`;
        alSeguirCon?.(ruta);
        try { await ejecutarFase(carpeta, ruta, previa.fase); alLanzar(); return; }
        catch (otra) { setPuerta((otra as Error).message); return; }
      }
      setPuerta((e as Error).message);
    }
    finally { setOcupado(false); }
  }

  // La versión nueva pasa a ser el manuscrito: la fase siguiente sale de ella.
  const hecho = trabajo?.estado === "hecho" ? trabajo.resultado as ResultadoDeFase : null;
  const nacida = hecho?.build ?? "";
  const hayQueSeguirCon = nacida && doc && nombreDeRuta(doc) !== nacida ? nacida : null;
  useEffect(() => {
    if (!hayQueSeguirCon || !alSeguirCon || !carpeta) return;
    alSeguirCon(`${carpeta}/${hayQueSeguirCon}`);
  }, [hayQueSeguirCon, alSeguirCon, carpeta]);

  const desdeHace = (() => {
    if (!enCurso || !trabajo?.inicio) return "";
    const min = Math.max(0, Math.round((Date.now() - Date.parse(trabajo.inicio)) / 60000));
    const hora = new Date(trabajo.inicio).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    return ` Empezó a las ${hora}${min >= 1 ? ` y lleva ${min} ${min === 1 ? "minuto" : "minutos"}` : ""}.`;
  })();

  const pasadas = previa?.pasadas ?? [];
  const palabras = previa?.palabras ?? 0;
  const coste = palabras > 0 ? estimarCoste(palabras, precio, undefined, "reescritura").coste : null;
  const avisos = avisosDeFaseHecha(hecho);

  return (
    <>
      {hecho && (
        <section className="carta">
          <h2>Fase {hecho.fase} hecha<span className="sub">{hecho.aplicadas} {hecho.aplicadas === 1 ? "cambio" : "cambios"}</span></h2>
          <p className="vacio">
            Se ha creado <b>{hecho.build}</b> con los cambios marcados{hecho.delta ? ` (${hecho.delta > 0 ? "+" : ""}${hecho.delta.toLocaleString("es-ES")} palabras)` : ""}. Tu versión anterior sigue intacta.
          </p>
          {!!hecho.pasadas?.length && (
            <ul className="lista-fase">
              {hecho.pasadas.map((p) => (
                <li key={p.n}><b>Pasada {p.n}</b> · {p.titulo}<span className="sub">{p.cambios} {p.cambios === 1 ? "cambio" : "cambios"}</span></li>
              ))}
            </ul>
          )}
          {avisos.map((a, i) => <p key={i} className="vacio" style={{ color: "var(--acc)" }}>{a}</p>)}
          {/* Del tablero de notas no se dice nada: en el flujo no está a la vista (el motor lo sigue llevando). */}
          <ol className="pasos">
            <li>Abre <b>{hecho.build}</b> en Word.</li>
            <li>{comoDecidirEnWord(hecho)}</li>
            <li>Guarda y vuelve aquí: la fase siguiente sale de esa versión.</li>
          </ol>
        </section>
      )}
      <section className="carta">
        {!previa ? (
          <>
            <h2>La fase que toca</h2>
            {sinLeer ? (
              // No se queda en «Mirando…» para siempre (equipo rojo, 3-oct): se dice y hay dos salidas.
              <>
                <p className="aviso">No he podido leer qué fase toca: el motor no ha contestado bien. Puede estar reiniciándose.</p>
                <div className="botones">
                  {alReintentar && <button className="btn" onClick={alReintentar}>Volver a intentarlo</button>}
                  <button className="btn texto" onClick={alIrDeUnaEnUna}>Hacer solo una pasada</button>
                </div>
              </>
            ) : <p className="vacio">Mirando el plan…</p>}
          </>
        ) : previa.fase == null ? (
          previa.codigo === "sin_pasadas" ? (
            <>
              <h2>Todas las fases del plan están hechas</h2>
              <div className="botones"><button className="btn" onClick={() => alIr("correccion")}>Ir a Corrección</button></div>
            </>
          ) : (
            <>
              <h2>La fase que toca</h2>
              <p className="vacio">{previa.codigo === "sin_plan" ? "Todavía no hay plan de reescritura: las fases salen de él." : previa.error ?? "No he podido leer el plan."}</p>
              {previa.codigo === "sin_plan" && <div className="botones"><button className="btn" onClick={() => alIr("plan")}>Ir al plan</button></div>}
            </>
          )
        ) : (
          <>
            <h2>Fase {previa.indice} de {previa.de} · {previa.area}
              <span className="sub">{pasadas.length} {pasadas.length === 1 ? "pasada" : "pasadas"}</span>
            </h2>
            <p className="vacio">
              {pasadas.length === 1 ? "Se hace" : "Se hacen seguidas, cada una sobre lo que deja la anterior, y sale"} una sola versión nueva con los cambios
              marcados. Se escribe sobre <b>{doc ? nombreDeRuta(doc) : "tu manuscrito"}</b>, que no se toca.
            </p>
            <ul className="lista-fase">
              {pasadas.map((p) => (
                <li key={p.n}><b>Pasada {p.n}</b> · {p.titulo}<span className="sub">{lineaDePasadaDeFase(p)}</span></li>
              ))}
            </ul>
            {trabajo && trabajo.estado !== "hecho" && <ListaDePasos trabajo={trabajo} />}
            {/* Lo que falló va ANTES del botón: es lo primero que hay que leer para decidir si se relanza. */}
            {trabajo?.estado === "fallido" && <p className="aviso" style={{ borderColor: "var(--acc)" }}><b style={{ color: "var(--acc)" }}>La fase no se completó.</b> {trabajo.error}</p>}
            {trabajo?.estado === "interrumpido" && (
              <p className="aviso" style={{ borderColor: "var(--acc)" }}>
                <b style={{ color: "var(--acc)" }}>La fase se cortó</b> al reiniciarse el motor y no ha creado ninguna versión. Vuelve a lanzarla: lo que ya volvió entero no se paga otra vez.
              </p>
            )}
            {enCurso ? (
              <p className="vacio">
                Se está haciendo la fase{trabajo?.progreso ? `: paso ${Math.min(trabajo.progreso.hecho + 1, trabajo.progreso.total)} de ${trabajo.progreso.total}` : "…"}.
                {desdeHace}{" "}Cada pasada son una o varias llamadas al modelo y tarda unos minutos; puedes cerrar el Studio, sigue en tu ordenador.
              </p>
            ) : !previa.aprobado ? (
              <>
                <p className="aviso">Antes hay que aprobar el plan: es la lista de lo que se va a hacer con tu libro. Puedes quitar lo que no quieras y aprobar el resto.</p>
                <div className="botones"><button className="btn" onClick={() => alIr("plan")}>Ir al plan</button></div>
              </>
            ) : previa.no_se_puede ? (
              // Se sabe ANTES de pulsar, no en la tercera pasada con dos ya pagadas.
              <>
                <p className="aviso">Esta fase no se puede hacer tal como está: los capítulos de alguna pasada no coinciden con los de tu manuscrito. Edita esa pasada en el plan, o rehazlo.</p>
                <div className="botones"><button className="btn" onClick={() => alIr("plan")}>Ir al plan</button></div>
              </>
            ) : (
              <div className="botones">
                <button className="btn" disabled={!doc || ocupado} onClick={hacer}>Hacer la fase {previa.indice}</button>
                <span className="vacio">
                  {palabras > 0 ? `~${palabras.toLocaleString("es-ES")} palabras` : previa.necesita_llave ? "" : "No gasta de tu llave"}
                  {coste != null ? ` · ~${dolares(coste)} de tu llave` : ""}
                </span>
                <button className="btn texto" onClick={alHablar}>Preguntar antes al coach</button>
                {pasadas.length > 1 && <button className="btn texto" onClick={alIrDeUnaEnUna}>Hacer solo una pasada</button>}
              </div>
            )}
            {pasadas.length > 1 && palabras > 0 && !enCurso && (
              <p className="vacio">La cifra es una estimación: de la segunda pasada en adelante se cuenta sobre el texto de hoy, y la primera puede alargarlo o acortarlo.</p>
            )}
          </>
        )}
        {puerta && (
          <div className="aviso" style={{ borderColor: "var(--acc)" }}>
            <b style={{ color: "var(--acc)" }}>La fase no se ha hecho.</b> {puerta}
            <div className="botones">
              <button className="btn" disabled={ocupado} onClick={hacer}>{ocupado ? "Comprobando…" : "Ya lo he hecho · vuelve a intentarlo"}</button>
              {doc && (
                <button className="btn texto" onClick={() => { navigator.clipboard?.writeText(doc).catch(() => {}); }}>
                  Copiar la ruta del fichero
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
